import * as React from 'react';
import { css, styled } from 'styled-components';

import { Dropdown, DropdownOption, DropdownGroup, tk } from '@sunwebgroup/ui-components';
import { useIntl } from '@/intl';
import { capitalize } from '@/helpers/string-helpers';
import { calculateDayRange, calculateMonthRange, calculateYearRange } from './dateHelpers';

interface DateDropdownProps extends React.HTMLAttributes<HTMLDivElement> {
  value: Date;
  min: Date;
  max?: Date;
  onValueChange?: (newDate: Date) => void;
}

function DateDropdown({ value, min, max = new Date(), onValueChange, className }: DateDropdownProps) {
  const intl = useIntl();

  const yearRange = React.useMemo(() => calculateYearRange(min, max), [min, max]);
  const monthRange = React.useMemo(() => calculateMonthRange(value, min, max), [value, min, max]);
  const dayRange = React.useMemo(() => calculateDayRange(value, min, max), [value, min, max]);

  const handleDayChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDate = new Date(value.getFullYear(), value.getMonth(), parseInt(e.target.value, 10));
    onValueChange?.(newDate);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDate = new Date(value.getFullYear(), parseInt(e.target.value, 10) - 1, value.getDate());

    if (newDate < min || newDate > max) {
      const allowedRange = calculateDayRange(newDate, min, max);

      newDate.setDate(allowedRange[0]);
    }

    onValueChange?.(newDate);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDate = new Date(parseInt(e.target.value, 10), value.getMonth(), value.getDate());

    const newDateIsOutOfRange = newDate < min || newDate > max;
    const newDateMonthIsOutOfRange = newDate.getMonth() < min.getMonth() || newDate.getMonth() > max.getMonth();

    if (newDateIsOutOfRange && newDateMonthIsOutOfRange) {
      const allowedRange = calculateMonthRange(newDate, min, max);
      const allowedMonth = allowedRange[allowedRange.length - 1] - 1;

      newDate.setMonth(allowedMonth);
    }

    if (newDate < min || newDate > max) {
      const allowedRange = calculateDayRange(newDate, min, max);

      newDate.setDate(allowedRange[0]);
    }

    onValueChange?.(newDate);
  };

  const getMonthName = (month: number) => {
    const date = new Date(2000, month - 1, 1);
    return intl.formatDate(date, { month: 'long' });
  };

  return (
    <Wrapper className={className}>
      <DropdownGroup>
        <Dropdown value={String(value.getDate())} onChange={handleDayChange} usePlaceholder={false}>
          {dayRange.map((day) => (
            <DropdownOption key={day} value={day}>
              {day}
            </DropdownOption>
          ))}
        </Dropdown>
        <Dropdown value={String(value.getMonth() + 1)} onChange={handleMonthChange} usePlaceholder={false}>
          {monthRange.map((month) => (
            <DropdownOption key={month} value={month} label={capitalize(getMonthName(month))}>
              {month}
            </DropdownOption>
          ))}
        </Dropdown>
        <Dropdown value={String(value.getFullYear())} onChange={handleYearChange} usePlaceholder={false}>
          {yearRange.map((year) => (
            <DropdownOption key={year} value={year}>
              {year}
            </DropdownOption>
          ))}
        </Dropdown>
      </DropdownGroup>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  select {
    -webkit-padding-end: ${tk('spacing_xl')};
    -webkit-padding-start: ${tk('spacing_sm')};
    background: transparent;
  }

  @media ${tk('mq_desktop_sm')} {
    ${modDayAndYearDropdownSize}
  }

  @media (max-width: 450px) {
    ${modDayAndYearDropdownSize}
  }
`;

function modDayAndYearDropdownSize() {
  return css`
    ${Dropdown} {
      &:first-child {
        width: ${tk('spacing_5xl')};
        select {
          width: ${tk('spacing_5xl')};
        }
      }

      &:last-child {
        width: 100px;
        select {
          width: 100px;
        }
      }
    }
  `;
}

export default styled(DateDropdown)``;
